import React, {useState, useMemo} from 'react';
import {Spin} from 'antd';
import DocumentActions from '@/views/posts/DocumentActions';
import {showErrorNotification, showSuccessNotification} from '@/shared/utils/notify';
import {useLocation, useNavigate} from "react-router-dom";
import {useQueryClient} from '@tanstack/react-query';
import {useApprove, useGetActionFlowByScopeId} from "@/shared/api/client/action-flow";
import {useGetLoanById} from "@/shared/api/client/appappro-management";
import ApprovalPage from "@/modules/action-flow/runtime/components/requests/ApprovalPage";
import ApprovalDecision from "@/modules/action-flow/runtime/components/requests/ApprovalDecision";
import LoanScheduleView from "@/modules/loans/components/LoanScheduleView";
import LoanGuarantorView from "@/modules/loans/components/LoanGuarantorView";
import LoanDocumentsView from "@/modules/loans/components/LoanDocumentsView";
import LoanOfficerRecommendationView from "@/modules/loans/components/LoanOfficerRecommendationView";
import DeductionFormView from "@/modules/loans/components/DeductionFormView";
import DocumentUploadView from "@/modules/loans/components/DocumentUploadView";
import OfficerDeductionFormGenerationView from "@/modules/loans/components/OfficerDeductionFormGenerationView";
import LoanOfficerViewWithProvider from "@/modules/loans/components/LoanOfficerViewWithProvider";
import LoanDetailsViewWithProvider from "@/modules/loans/components/LoanDetailsViewWithProvider";
import LoanSupervisorReviewViewWithProvider
    from "@/modules/loans/components/LoanSupervisorReviewViewWithProvider";
import LoanAgreementView from "@/modules/loan-product-settings/components/loan-agreement/LoanAgreementView";
import {
    ESSLoanDetailsView,
    ESSEmployeeInfoView,
    ESSFinancialInfoView,
    ESSQualificationAdviceView,
    ESSTakeoverInfoView,
    ESSLoanSummaryView,
    ESSDebugView
} from "@/modules/loans/components/ess";

const getLoanIdFromRecordId = (recordId?: string | null) => {
    if (!recordId) {
        return '';
    }

    const candidate = recordId.split('.').pop()?.trim() ?? '';
    return /^\d+$/.test(candidate) ? candidate : '';
};

const hasValueData = (valueData: any) => {
    if (!valueData || typeof valueData !== 'object') {
        return false;
    }

    return Object.keys(valueData).length > 0;
};

const ActionFlowApprovalView: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const scopeId = location.pathname.split('/').pop() || '';
    const [currentStep, setCurrentStep] = useState(0);

    const {data: response, isLoading, error} = useGetActionFlowByScopeId(scopeId);
    const {mutateAsync} = useApprove();
    const actionFlow = response?.data;
    const fallbackLoanId = useMemo(() => {
        if (hasValueData(actionFlow?.valueData)) {
            return '';
        }

        return getLoanIdFromRecordId(actionFlow?.recordId);
    }, [actionFlow?.recordId, actionFlow?.valueData]);
    const {
        data: fallbackLoanData,
        isLoading: isFallbackLoanLoading,
    } = useGetLoanById(fallbackLoanId, {
        query: {
            enabled: !!fallbackLoanId,
        },
    });

    // Memoize customComponents to prevent infinite re-renders
    const customComponents = useMemo(() => ({
        DocumentActions: (props: any) => <DocumentActions {...props} />,
        LoanScheduleView: (props: any) => <LoanScheduleView {...props} />,
        LoanGuarantorView: (props: any) => <LoanGuarantorView {...props} />,
        LoanDetailsView: (props: any) => <LoanDetailsViewWithProvider {...props} />,
        LoanOfficerView: (props: any) => <LoanOfficerViewWithProvider {...props} />,
        ApprovalDecision: (props: any) => <ApprovalDecision {...props} />,
        LoanDocumentsView: (props: any) => <LoanDocumentsView {...props} />,
        LoanOfficerRecommendationView: (props: any) => <LoanOfficerRecommendationView {...props} />,
        DeductionFormView: (props: any) => <DeductionFormView {...props} />,
        OfficerDeductionFormGenerationView: (props: any) => <OfficerDeductionFormGenerationView {...props} />,
        DocumentUploadView: (props: any) => <DocumentUploadView {...props} />,
        LoanSupervisorReviewView: (props: any) => <LoanSupervisorReviewViewWithProvider {...props} />,
        LoanAgreementView: (props: any) => <LoanAgreementView {...props} />,
        // ESS Integration Components
        ESSLoanDetailsView: (props: any) => <ESSLoanDetailsView {...props} />,
        ESSEmployeeInfoView: (props: any) => <ESSEmployeeInfoView {...props} />,
        ESSFinancialInfoView: (props: any) => <ESSFinancialInfoView {...props} />,
        ESSQualificationAdviceView: (props: any) => <ESSQualificationAdviceView {...props} />,
        ESSTakeoverInfoView: (props: any) => <ESSTakeoverInfoView {...props} />,
        ESSLoanSummaryView: (props: any) => <ESSLoanSummaryView {...props} />,
        ESSDebugView: (props: any) => <ESSDebugView {...props} />
    }), []); // Empty dependency array since these components don't depend on any props or state

    if (isLoading || !response || isFallbackLoanLoading) {
        return <Spin size="large"/>;
    }

    if (error || !actionFlow || !actionFlow.domain || !actionFlow.subscriptionId) {
        return <div>No data found</div>;
    }

    const valueData = hasValueData(actionFlow.valueData) ? actionFlow.valueData : fallbackLoanData ?? actionFlow.valueData ?? {};
    const changesData = actionFlow.changesData ?? {};
    const beforeData = actionFlow.beforeData ?? {};
    const initialData = {
        ...actionFlow,
        valueData,
        changesData,
        beforeData,
    };

    const handleSubmit = async (values: any) => {
        try {
            if (!scopeId) return;

            const submittedValueData = hasValueData(values?.valueData) ? values.valueData : valueData;
            await mutateAsync({
                id: scopeId,
                data: {
                    ...values,
                    valueData: submittedValueData,
                    updatedValueData: submittedValueData,
                    updateStoredValue: hasValueData(submittedValueData),
                },
            });

            showSuccessNotification('Request processed successfully');

            // Invalidate pending requests cache so list refreshes after navigation
            await queryClient.invalidateQueries({ queryKey: ['/api/v1/action-flows/pending-to-approve'] });

            navigate('/requests/loans/pending', { state: { refreshed: Date.now() } });
        } catch (error) {
            showErrorNotification('Failed to process request');
        }
    };

    return (
        <ApprovalPage
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            initialData={initialData}
            customComponents={customComponents}
            onSubmit={handleSubmit}
            domain={actionFlow.domain}
            subscriptionId={actionFlow.subscriptionId}
            nodeId={actionFlow.state}
        />
    );
};

export default ActionFlowApprovalView;